import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { auth } from './config/firebase';
import PrivacyPolicy from './features/privacy/PrivacyPolicy';
import ContactForm from './features/contact/ContactForm';
import AboutPage from './features/about/AboutPage';
import { ThemeProvider } from '@mui/material/styles';
import { Box, Container, useMediaQuery, useTheme, CssBaseline, Button, Slide } from '@mui/material';
import Footer from './features/footer/Footer';
import AuthModal from './features/auth/AuthModal';
import WelcomeModal from './features/auth/WelcomeModal';
import RequireAuthModal from './features/auth/RequireAuthModal';
import { setUser, setShowWelcomeModal, resetCloseAuthDialog } from './features/auth/authSlice';
import AdminLayout from './features/admin/AdminLayout';
import TournamentList from './features/admin/TournamentList';
import TournamentForm from './features/admin/TournamentForm';
import TournamentNameForm from './features/admin/TournamentNameForm';
import TeamNameForm from './features/admin/TeamNameForm';
import TournamentDraftList from './features/admin/TournamentDraftList';
import UserSettings from './features/auth/UserSettings';
import FilterPage from './features/filters/FilterPage';
import './App.css';

// 分離したコンポーネントとフック
import { theme } from './styles/theme';
import Header from './components/header/Header';
import Calendar from './components/calendar/Calendar';
import EventDetailModal from './components/modals/EventDetailModal';
import SEO from './components/seo/SEO';
import StructuredData from './components/seo/StructuredData';
import { useTournamentData } from './hooks/useTournamentData';
import { useUserFilters } from './hooks/useUserFilters';

// 月の名前
const monthNames = ["1月", "2月", "3月", "4月", "5月", "6月",
                   "7月", "8月", "9月", "10月", "11月", "12月"];

function App() {
  const location = useLocation();
  const dispatch = useDispatch();
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // ページ遷移時にトップにスクロール
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  const { user, showWelcomeModal, closeAuthDialog } = useSelector((state) => state.auth);
  const filters = useSelector((state) => state.filters);
  const [currentDate, setCurrentDate] = useState(new Date());
  const today = useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);
  const [slideDirection, setSlideDirection] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [requireAuthModal, setRequireAuthModal] = useState(false);
  const [showTodayButton, setShowTodayButton] = useState(false);
  const [isTodayInPast, setIsTodayInPast] = useState(false);
  const [isDatePickerSelection, setIsDatePickerSelection] = useState(false);

  // カスタムフックを使用
  const { currentTournaments, isLoading } = useTournamentData(currentDate);
  useUserFilters(user); // フィルターをReduxストアに読み込む

  const handleRequireAuth = useCallback(() => {
    setRequireAuthModal(true);
  }, []);

  const handleLoginClick = useCallback(() => {
    setRequireAuthModal(false);
    setAuthModalOpen(true);
  }, []);

  const handleAuthModalClose = useCallback(() => {
    setAuthModalOpen(false);
    dispatch(resetCloseAuthDialog());
  }, [dispatch]);

  const handleAuthClick = useCallback(() => {
    setAuthModalOpen(true);
  }, []);

  // ログイン成功時にモーダルを閉じる
  useEffect(() => {
    if (closeAuthDialog) {
      handleAuthModalClose();
    }
  }, [closeAuthDialog, handleAuthModalClose]);

  // ウェルカムモーダルを3秒後に自動的に閉じる
  useEffect(() => {
    if (showWelcomeModal) {
      const timer = setTimeout(() => {
        dispatch(setShowWelcomeModal(false));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showWelcomeModal, dispatch]);

  // 認証状態の監視
  useEffect(() => {
    console.log('認証状態監視を開始');
    
    const unsubscribe = auth.onAuthStateChanged((user) => {
      console.log('認証状態が変更されました:', user);
      if (user) {
        console.log('ユーザー情報:', {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          providerId: user.providerId,
          providerData: user.providerData
        });
        dispatch(setUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName
        }));
      } else {
        console.log('ユーザーがログアウトまたはセッションが切れました');
        dispatch(setUser(null));
      }
    });

    return () => {
      console.log('認証状態監視を終了');
      unsubscribe();
    };
  }, [dispatch]);

  const handleMonthChange = useCallback(async (directionOrDate) => {
    let newDate;
    
    // 日付オブジェクトが直接渡された場合
    if (directionOrDate instanceof Date) {
      newDate = new Date(directionOrDate);
      // DatePickerからの選択であることを記録
      setIsDatePickerSelection(true);
      // 現在の月と比較してスライド方向を決定
      if (newDate.getTime() < currentDate.getTime()) {
        setSlideDirection('right');
      } else if (newDate.getTime() > currentDate.getTime()) {
        setSlideDirection('left');
      }
    } else {
      // 文字列（'prev' または 'next'）が渡された場合
      newDate = new Date(currentDate);
      if (directionOrDate === 'prev') {
        newDate.setMonth(newDate.getMonth() - 1);
        const oneYearAgo = new Date(today);
        oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
        if (newDate < oneYearAgo) return;
        setSlideDirection('right');
      } else {
        newDate.setMonth(newDate.getMonth() + 1);
        const threeMonthsAhead = new Date(today);
        threeMonthsAhead.setMonth(threeMonthsAhead.getMonth() + 3);
        if (newDate > threeMonthsAhead) return;
        setSlideDirection('left');
      }
    }

    setCurrentDate(newDate);
    
    // アニメーション完了後にslideDirectionをリセット
    setTimeout(() => {
      setSlideDirection(null);
    }, 300);
  }, [currentDate, today]);

  const handleEventClick = useCallback((event) => {
    setSelectedEvent(event);
  }, []);

  // Todayボタンのクリックハンドラー
  const handleTodayClick = useCallback(() => {
    // 今月でない場合は今月に移動
    if (
      currentDate.getFullYear() !== today.getFullYear() ||
      currentDate.getMonth() !== today.getMonth()
    ) {
      handleMonthChange(today);
    } else {
      // 既に今月の場合は今日の日付にスクロール
      const todayElement = document.querySelector('[data-is-today="true"]');
      if (todayElement) {
        const headerHeight = 140;
        const top = todayElement.getBoundingClientRect().top;
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        const targetPosition = Math.max(0, scrollTop + top - headerHeight);
        
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [currentDate, today, handleMonthChange]);

  // スクロール検知でTodayボタンの表示/非表示を制御
  useEffect(() => {
    // 当月以外を見ている場合は常に表示
    const isNotCurrentMonth = 
      currentDate.getFullYear() !== today.getFullYear() ||
      currentDate.getMonth() !== today.getMonth();
    
    if (isNotCurrentMonth) {
      setShowTodayButton(true);
      setIsTodayInPast(false);
      return;
    }
    
    // 当月の場合はスクロール位置で制御
    const handleScroll = () => {
      const scrollY = window.scrollY;
      const headerHeight = isMobile ? 200 : 250;
      
      // ヘッダーの下にスクロールしたらボタンを表示
      setShowTodayButton(scrollY > headerHeight);
      
      // 今日の位置を判定
      const todayElement = document.querySelector('[data-is-today="true"]');
      if (todayElement) {
        const rect = todayElement.getBoundingClientRect();
        // 今日の要素が画面の上にある（スクロールで過ぎ去った）場合
        setIsTodayInPast(rect.bottom < 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // 初期状態をチェック

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isMobile, currentDate, today]);

  // 今月表示時に今日の日付にスクロール（DatePicker選択時は除く）
  useEffect(() => {
    if (
      !isLoading &&
      !isDatePickerSelection &&
      currentDate.getFullYear() === today.getFullYear() &&
      currentDate.getMonth() === today.getMonth()
    ) {
      const timer = setTimeout(() => {
        const todayElement = document.querySelector('[data-is-today="true"]');
        if (todayElement) {
          const headerHeight = 140;

          const initialTop = todayElement.getBoundingClientRect().top;
          const initialScroll = window.pageYOffset || document.documentElement.scrollTop;
          const initialPosition = Math.max(0, initialScroll + initialTop - headerHeight);
          window.scrollTo({
            top: initialPosition,
            behavior: 'auto'
          });

          requestAnimationFrame(() => {
            const finalTop = todayElement.getBoundingClientRect().top;
            const finalScroll = window.pageYOffset || document.documentElement.scrollTop;
            const finalPosition = Math.max(0, finalScroll + finalTop - headerHeight);
            window.scrollTo({
              top: finalPosition,
              behavior: 'smooth'
            });
          });
        }
      }, 400);

      return () => clearTimeout(timer);
    }
    
    // DatePicker選択フラグをリセット
    if (isDatePickerSelection) {
      setIsDatePickerSelection(false);
    }
  }, [currentDate, isLoading, today, isDatePickerSelection]);

  // ヘッダーの高さを動的に計算
  const getHeaderPadding = () => {
    if (isMobile && filters.isActive) {
      return 20; // モバイルでフィルター適用時は追加の余白
    }
    return isMobile ? 18 : 20;
  };

  return (
    <HelmetProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box className="cyber-grid" />
        <Box className="scanlines" />
        <Routes>
        <Route path="/admin/*" element={<AdminLayout />}>
          <Route path="tournaments" element={<TournamentList />} />
          <Route path="tournaments/new" element={<TournamentForm />} />
          <Route path="tournaments/name/new" element={<TournamentNameForm />} />
          <Route path="tournaments/team/new" element={<TeamNameForm />} />
          <Route path="tournaments/edit/:id" element={<TournamentForm />} />
          <Route path="drafts" element={<TournamentDraftList />} />
          <Route path="tournament-names" element={<TournamentNameForm />} />
          <Route path="team-names" element={<TeamNameForm />} />
        </Route>
        <Route path="/settings" element={
          <>
            <SEO title="設定" description="ClachSyncのユーザー設定ページ。通知設定やアカウント管理ができます。" />
            <UserSettings />
          </>
        } />
        <Route path="/privacy-policy" element={
          <>
            <SEO title="プライバシーポリシー" description="ClachSyncのプライバシーポリシー。お客様の個人情報の取り扱いについて説明しています。" />
            <PrivacyPolicy />
          </>
        } />
        <Route path="/contact" element={
          <>
            <SEO title="お問い合わせ" description="ClachSyncへのお問い合わせページ。ご質問やご要望をお聞かせください。" />
            <ContactForm />
          </>
        } />
        <Route path="/about" element={
          <>
            <SEO title="ClachSyncについて" description="ClachSyncの特徴や使い方について。eスポーツファンのための大会スケジュール管理アプリです。" />
            <AboutPage />
          </>
        } />
        <Route path="/filters" element={
          <>
            <SEO title="フィルター設定" description="お気に入りの大会やチームを選択して、カスタマイズされたスケジュールを作成できます。" />
            <FilterPage />
          </>
        } />
        <Route path="/" element={
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            <SEO />
            <StructuredData type="website" />
            <StructuredData type="organization" />
            <Header
              currentDate={currentDate}
              onMonthChange={handleMonthChange}
              onAuthClick={handleAuthClick}
              onRequireAuth={handleRequireAuth}
              monthNames={monthNames}
              today={today}
            />
            
            {/* Todayボタン - ヘッダー直下に表示 */}
            <Slide direction="down" in={showTodayButton} mountOnEnter unmountOnExit>
              <Box
                sx={{
                  position: 'fixed',
                  top: filters.isActive && isMobile ? '155px' : isMobile ? '130px' : '165px', // ヘッダーぎりぎりに調整
                  left: 0,
                  right: 0,
                  zIndex: 1100,
                  display: 'flex',
                  justifyContent: 'center',
                  bgcolor: 'transparent',
                  pointerEvents: 'none', // 他の要素のクリックを妨げない
                }}
              >
                <Button
                  onClick={handleTodayClick}
                  sx={{
                    bgcolor: 'rgba(0, 255, 255, 0.9)',
                    color: '#000',
                    fontWeight: 700,
                    px: isMobile ? 3 : 4,
                    py: 0.5,
                    fontSize: isMobile ? '0.875rem' : '1rem',
                    letterSpacing: '0.05em',
                    borderRadius: '0 0 20px 20px',
                    boxShadow: `0 4px 20px ${theme.palette.neon.cyan}`,
                    backdropFilter: 'blur(10px)',
                    pointerEvents: 'auto', // ボタン自体はクリック可能
                    '&:hover': {
                      bgcolor: theme.palette.neon.cyan,
                      transform: 'translateY(2px)',
                      boxShadow: `0 6px 30px ${theme.palette.neon.cyan}`,
                    },
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isTodayInPast ? '↓' : '↑'} TODAY
                </Button>
              </Box>
            </Slide>
            
            <Container maxWidth="xl" sx={{ pt: getHeaderPadding(), pb: 2 }}>
              <Calendar
                currentDate={currentDate}
                currentTournaments={currentTournaments}
                isLoading={isLoading}
                slideDirection={slideDirection}
                onMonthChange={handleMonthChange}
                onEventClick={handleEventClick}
                today={today}
              />

              <EventDetailModal
                selectedEvent={selectedEvent}
                onClose={() => setSelectedEvent(null)}
              />

              {/* 認証モーダル */}
              <AuthModal
                open={authModalOpen}
                onClose={handleAuthModalClose}
              />

              {/* ウェルカムモーダル */}
              <WelcomeModal
                open={showWelcomeModal}
                username={user?.email?.split('@')[0] || user?.displayName || 'ゲスト'}
              />

              <RequireAuthModal
                open={requireAuthModal}
                onClose={() => setRequireAuthModal(false)}
                onLogin={handleLoginClick}
              />
            </Container>
            
            <Footer />
          </Box>
        } />
      </Routes>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
